"use client";

import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/utils/shadcn";
import { useLogs } from "@/hooks/useLogs";
import Editor from "@monaco-editor/react";
import type { ParsedLogMessage } from "@/models/logs";

const LOG_LEVELS = {
  INFO: { label: "INFO", color: "bg-info" },
  WARN: { label: "WARN", color: "bg-warning" },
  ERROR: { label: "ERROR", color: "bg-destructive" },
  FATAL: { label: "FATAL", color: "bg-violet" }
} as const;

interface DisplayLogEntry {
  id: string;
  timestamp: Date;
  level: keyof typeof LOG_LEVELS;
  message: string;
  logStreamName: string;
  metadata?: Record<string, unknown>;
  year: string;
  month: string;
  day: string;
  hour: string;
}

export function LogsViewer() {
  // SQL Query state
  const [sqlQuery, setSqlQuery] = useState<string>(
    `SELECT * 
FROM waba_logs
WHERE year = 2025 
  AND month = 10 
  AND day = 12
  AND level IN ('INFO')
ORDER BY timestamp DESC;`
  );
  const [submittedQuery, setSubmittedQuery] = useState<string>("");

  // Display state
  const [logs, setLogs] = useState<DisplayLogEntry[]>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  const toggleMetadata = (logId: string) => {
    setExpandedLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  // Fetch logs with mutation
  const { data: logsData, isPending: isLoading, mutate: fetchLogs } = useLogs();

  // Update logs when data changes
  useEffect(() => {
    if (!logsData?.rows) {
      setLogs([]);
      return;
    }

    const transformedLogs = logsData.rows.map((row) => {
      let parsed: ParsedLogMessage = {};
      try {
        parsed = JSON.parse(row.message);
      } catch {
        // If parsing fails, use the raw message
      }

      return {
        id: row.id,
        timestamp: new Date(Number(row.timestamp)),
        level: row.level as keyof typeof LOG_LEVELS,
        message: row.message,
        logStreamName: row.log_stream,
        metadata: parsed,
        year: row.year,
        month: row.month,
        day: row.day,
        hour: row.hour
      };
    });

    setLogs(transformedLogs);
  }, [logsData]);

  const handleExecuteQuery = () => {
    if (sqlQuery.trim()) {
      setSubmittedQuery(sqlQuery);
      fetchLogs({ query: sqlQuery });
    }
  };

  return (
    <div className="space-y-6">
      {/* SQL Query Input */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sql-query">SQL Query</Label>
              <div className="border rounded-md overflow-hidden">
                <Editor
                  height="300px"
                  defaultLanguage="sql"
                  value={sqlQuery}
                  onChange={(value) => setSqlQuery(value || "")}
                  theme="vs-dark"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineNumbers: "on",
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    wordWrap: "on",
                    padding: { top: 10, bottom: 10 },
                    suggestOnTriggerCharacters: true,
                    quickSuggestions: true,
                    suggest: {
                      showKeywords: true,
                      showSnippets: true
                    }
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Example: SELECT * FROM waba_logs WHERE year = 2025 AND month = 10 AND day &gt; 12
                AND level = 'INFO' ORDER BY timestamp DESC;
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleExecuteQuery} disabled={!sqlQuery.trim()}>
                Execute Query
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Display */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Logs ({logs.length})</h3>
            {logsData?.statistics && (
              <div className="text-xs text-muted-foreground">
                Scanned: {(logsData.statistics.dataScannedInBytes / 1024).toFixed(2)} KB •
                Execution: {logsData.statistics.executionTimeInMillis}ms
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">Loading logs...</p>
            </div>
          ) : !submittedQuery ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                Enter and execute a SQL query to view logs
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[800px] overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No logs found for this query.
                </p>
              ) : (
                <>
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      onClick={() => toggleMetadata(log.id)}
                      className="p-4 border border-border rounded-lg transition-colors font-mono text-sm cursor-pointer hover:bg-accent"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full",
                              LOG_LEVELS[log.level]?.color || "bg-gray-500"
                            )}
                          />
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            {log.metadata && Object.keys(log.metadata).length > 0 && (
                              <span>
                                {expandedLogs.has(log.id) ? (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                )}
                              </span>
                            )}
                            <Badge variant="secondary" className="text-xs">
                              {log.level}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {log.logStreamName}
                            </span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {format(log.timestamp, "MMM dd, yyyy HH:mm:ss")}
                            </span>
                          </div>
                          <p className="text-foreground break-words text-xs">{log.message}</p>
                          {log.metadata &&
                            Object.keys(log.metadata).length > 0 &&
                            expandedLogs.has(log.id) && (
                              <pre className="text-xs text-muted-foreground bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap">
                                {JSON.stringify(log.metadata, null, 2)}
                              </pre>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
